import React, { useCallback, type ComponentType, type SVGProps } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useProtectedAction } from "@/hooks/useProtectedAction";
import { User } from "lucide-react";
import { classNames } from "@/utils/classNames";

interface BottomNavigationItemProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  profileImage?: string;
  label: string;
  to?: string;
  onClick?: () => void;
  requiresAuth?: boolean;
  asButton?: boolean;
  selected?: boolean;
}

export function BottomNavigationItem({
  icon: IconComponent,
  profileImage,
  label,
  to,
  onClick,
  requiresAuth = false,
  asButton = false,
  selected = false,
}: BottomNavigationItemProps) {
  const { withAuth } = useProtectedAction();
  const navigate = useNavigate();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (requiresAuth) {
        e.preventDefault();
        withAuth(() => {
          if (onClick) onClick();
          if (to) navigate({ to });
        })();
      } else if (onClick) {
        e.preventDefault();
        onClick();
      }
    },
    [requiresAuth, withAuth, onClick, to, navigate]
  );

  const baseClass =
    "cursor-pointer flex items-center justify-center w-[54px] h-[54px]";
  const selectedClass = selected ? " text-black font-semibold" : "";
  const className = `${baseClass}${selectedClass}`;

  const content = (
    <>
      {IconComponent && (
        <div className="w-6 h-6 relative overflow-hidden">
          <IconComponent
            fill={selected && to === "/" ? "black" : "white"}
            stroke="black"
            strokeWidth={selected ? 2.5 : 1.5}
            className="absolute text-2xl"
          />
        </div>
      )}
      {label === "Profile" && (
        <div
          className={classNames(
            "w-7 h-7 flex items-center justify-center relative rounded-full overflow-hidden",
            selected && "border-2 border-black"
          )}
        >
          {profileImage !== "empty-image" && profileImage ? (
            <img
              src={profileImage}
              alt={label}
              className="w-full h-full object-cover object-center rounded-full"
            />
          ) : (
            <User className="text-black" size={16} />
          )}
        </div>
      )}
    </>
  );

  if (asButton) {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={handleClick}
        className={className}
      >
        {content}
      </button>
    );
  }

  return (
    <Link
      to={to || "/"}
      aria-label={label}
      aria-current={selected ? "page" : undefined}
      className={className}
      onClick={handleClick}
      preload="render"
    >
      {content}
    </Link>
  );
}
